/**
 * Local development ports. `scripts/dev.sh` serves the site on
 * `DEV_SITE_PORT` (Next, overridable with `PORT=`), and the admin SPA runs on
 * `DEV_ADMIN_PORT` (`pnpm --filter @podecontratar/admin dev`). The API worker
 * is on 8788 and the MCP worker on 8789, so none of the four collide.
 *
 * Keep `apps/admin/vite.config.ts`, the `dev`/`preview` scripts in both
 * packages' package.json, and `scripts/dev.sh` in step with these.
 */
export const DEV_SITE_PORT = 3001;
export const DEV_ADMIN_PORT = 3002;

function isLoopback(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

/**
 * Where the admin SPA lives, for the environment the caller is running in.
 *
 *   https://www.podecontratar.com  →  https://admin.podecontratar.com
 *   https://www.podecontratar.com  →  https://admin.podecontratar.com
 *   https://pc.zarv.dev            →  https://admin.pc.zarv.dev  (staging)
 *   http://localhost:3001          →  http://localhost:3002      (vite)
 *
 * The mirror of `websiteOrigin()` in the admin SPA, which strips the `admin.`
 * label to get back here. Derived rather than configured for the same reason:
 * a hardcoded admin URL sends a staging email to the production panel, which
 * is how an operator ends up acting on live data while testing.
 *
 * An `override` wins when supplied — see `ADMIN_URL` in the worker vars —
 * for the day the panel stops living at `admin.<site>`.
 *
 * `www.` is stripped first — `admin.www.podecontratar.com` does not exist.
 * Local development is a port change, not a subdomain, because vite serves
 * the admin on `DEV_ADMIN_PORT` next to the site on `DEV_SITE_PORT`.
 */
export function adminOriginForWebsite(publicUrl: string, override?: string): string {
  // An explicit value wins, for the day the panel does not live at
  // `admin.<site>` any more. Until then it stays unset on purpose: a second
  // source of truth is a second thing that can go stale, which is how the
  // deploy script spent months naming a staging host that had moved.
  const explicit = override?.trim();
  if (explicit) return explicit.replace(/\/+$/, "");

  let url: URL;
  try {
    url = new URL(publicUrl);
  } catch {
    // A malformed PUBLIC_URL is a configuration problem, not something to
    // paper over with a guess at the environment: production is the only
    // answer that cannot leak staging traffic into a live panel.
    return "https://admin.podecontratar.com";
  }

  if (isLoopback(url.hostname)) {
    return `${url.protocol}//${url.hostname}:${DEV_ADMIN_PORT}`;
  }

  const host = url.hostname.replace(/^www\./, "");
  return `${url.protocol}//admin.${host}`;
}

/**
 * The inverse: where the site lives, seen from the admin SPA's own origin.
 *
 *   https://admin.podecontratar.com  →  https://www.podecontratar.com
 *   https://admin.pc.zarv.dev        →  https://pc.zarv.dev       (staging)
 *   http://localhost:3002            →  http://localhost:3001     (next dev)
 *
 * Locally the hostname is kept as typed: cookies are scoped by host, not by
 * port, so an admin opened on 127.0.0.1 must send the person to a site on
 * 127.0.0.1 or the session cookie will not be there. `devOverride` (the
 * admin's `VITE_WEBSITE_ORIGIN`) wins on loopback, for a site started with a
 * non-default `PORT=`.
 *
 * Production lands on the canonical `www.` host directly; the bare apex only
 * answers with a 301 to it.
 */
export function websiteOriginForAdmin(adminOrigin: string, devOverride?: string): string {
  let url: URL;
  try {
    url = new URL(adminOrigin);
  } catch {
    return "https://www.podecontratar.com";
  }
  if (isLoopback(url.hostname)) {
    const explicit = devOverride?.trim();
    if (explicit) return explicit.replace(/\/+$/, "");
    return `${url.protocol}//${url.hostname}:${DEV_SITE_PORT}`;
  }
  const host = url.hostname.replace(/^admin\./, "");
  if (host === "podecontratar.com") return `${url.protocol}//www.${host}`;
  return `${url.protocol}//${host}`;
}
